import { DataGridConfig } from 'components/DataGrid';
import { useAtom } from 'jotai';
import moment from 'moment';
import React, { useMemo } from 'react';

import RowActions from './RowActions';
import {
  columnKeysAtom,
  DEFAULT_BPA_SCAN_COLUMN_KEYS,
  orderbyAtom,
  paginationConfigAtom,
  selectedRowIdsAtom,
  sortOrderAtom,
} from './state';
import { BPAScan, BPAScanStatus } from './types';
import { useBPAScanData } from './useBPAScanData';

/** Maps each scan status to a Bootstrap badge class. */
export const STATUS_BADGE_CLASS: Record<BPAScanStatus, string> = {
  Completed: 'badge-success',
  Failed: 'badge-danger',
  InProgress: 'badge-primary',
  Queued: 'badge-info',
  RetryPending: 'badge-warning',
};

/** Formats an ISO date string to a human-readable scan timestamp. */
const formatScanDate = (dateStr: string | null): string =>
  dateStr ? moment(dateStr).format('MMM DD, YYYY h:mm A') : '—';

/**
 * Static column definitions. Extracted to module level so they are never
 * recreated on pagination or sort changes — only the config wrapper re-memos.
 */
const BPA_SCAN_COLUMNS: DataGridConfig['columns'] = [
  {
    key: 'account_id',
    label: 'Account ID',
    sortable: true,
    headerTooltip: 'Cloud Account ID',
  },
  {
    key: 'integration_label',
    label: 'Integration Name',
    sortable: true,
    headerTooltip: 'Integration Name',
  },
  {
    key: 'region',
    label: 'Region',
    sortable: true,
    headerTooltip: 'Region of the scan',
  },
  {
    key: 'benchmark_description',
    label: 'Benchmark',
    sortable: true,
    headerTooltip: 'Benchmark targeted by the scan',
    overrideContents: (rowData: BPAScan) => (
      <span className="d-inline-flex align-items-center">
        <span>{rowData.benchmark_description || '—'}</span>
        {rowData.benchmark_deprecated && (
          <span className="badge badge-warning ml-2">Deprecated</span>
        )}
      </span>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    headerTooltip: 'Current status of the scan',
    overrideContents: (rowData: BPAScan) => (
      <span
        className={`badge ${STATUS_BADGE_CLASS[rowData.status.replace(/\s+/g, '') as BPAScanStatus]}`}
      >
        {rowData.status}
      </span>
    ),
  },
  {
    key: 'job_type',
    label: 'Scan Type',
    sortable: true,
    headerTooltip: 'Type of scan',
    overrideContents: (rowData: BPAScan) => (
      <span className="text-capitalize">
        {rowData.job_type ? rowData.job_type.replace(/-/g, ' ') : '—'}
      </span>
    ),
  },
  {
    key: 'trigger_type',
    label: 'Scan Mode',
    sortable: true,
    headerTooltip: 'Mode of scan',
    overrideContents: (rowData: BPAScan) => (
      <span className="text-capitalize">{rowData.trigger_type}</span>
    ),
  },
  {
    key: 't12_created_at',
    label: 'Created At',
    sortable: true,
    headerTooltip: 'Creation Date in Platform',
    render: (rowData: BPAScan) => formatScanDate(rowData.t12_created_at),
  },
  {
    key: 't12_updated_at',
    label: 'Updated At',
    sortable: true,
    headerTooltip: 'Last Updated Date in Platform',
    render: (rowData: BPAScan) => formatScanDate(rowData.t12_updated_at),
  },
  {
    key: 'started_at',
    label: 'Started At',
    sortable: true,
    headerTooltip: 'Scan start time',
    render: (rowData: BPAScan) => formatScanDate(rowData.started_at),
  },
  {
    key: 'finished_at',
    label: 'Finished At',
    sortable: true,
    headerTooltip: 'Scan finish time',
    render: (rowData: BPAScan) => formatScanDate(rowData.finished_at),
  },
  {
    key: 'scan_duration',
    label: 'Duration',
    sortable: true,
    orderBy: 'duration',
    headerTooltip: 'Total scan duration',
  },
  {
    key: 'actions',
    label: 'Actions',
    render: (rowData: BPAScan) => <RowActions rowData={rowData} />,
    headerTooltip: 'Actions',
    headerCellStyle: {
      textAlign: 'right',
      maxWidth: 'auto',
      minWidth: '100px !important',
    },
    rowCellStyle: {
      textAlign: 'right',
      minWidth: '100px !important',
      maxWidth: 'auto',
      whiteSpace: 'nowrap',
    },
  },
];

export const useBPAScanDataConfig = () => {
  const listingData = useBPAScanData();
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const [orderby, setOrderBy] = useAtom(orderbyAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom);
  const [, setSelectedRowIds] = useAtom(selectedRowIdsAtom);
  const [columnKeys, setColumnKeys] = useAtom(columnKeysAtom);

  // Derive primitives so the memo does not hold a reference to the full object.
  const { page, page_size } = paginationConfig;
  const totalCount = listingData?.count ?? 0;

  const config: DataGridConfig = useMemo(
    () => ({
      rowIdKey: 'id',
      columns: BPA_SCAN_COLUMNS,
      paginationConfig: {
        total_count: totalCount,
        pageNumber: page - 1,
        pageSize: page_size,
        onPageChange: (newPage: number) => {
          setPaginationConfig((prev) => ({ ...prev, page: newPage + 1 }));
          setSelectedRowIds([]);
        },
        onPageSizeChange: (newPageSize: number) => {
          setPaginationConfig((prev) => ({
            ...prev,
            page_size: newPageSize,
            page: 1,
          }));
          setSelectedRowIds([]);
        },
      },
      sortingConfig: {
        orderBy: orderby,
        order: sortOrder,
        onOrderByChange: (value: string) => setOrderBy(value),
        onOrderChange: (value: 'asc' | 'desc') => setSortOrder(value),
      },
      columnsDisplayConfig: {
        enabledColumnKeys: columnKeys,
        defaultColumnKeys: DEFAULT_BPA_SCAN_COLUMN_KEYS,
        onColumnsDisplayChange: setColumnKeys,
      },
    }),
    [
      totalCount,
      page,
      page_size,
      orderby,
      sortOrder,
      setPaginationConfig,
      setSelectedRowIds,
      setOrderBy,
      setSortOrder,
      columnKeys,
      setColumnKeys,
    ],
  );

  return { config, data: listingData?.results, loading: listingData.loading };
};
